import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { ApplyKycApprovalRequest } from "@/features/wallet/api/dto";

/**
 * Voie PRINCIPALE du worker kyc-service (`POST /Wallets/apply-kyc-approval`,
 * guide v2.5 §6.3 — miroir du consumer Kafka `kyc.case.approved`).
 *
 * Applique le palier KYC validé à **tous** les portefeuilles du titulaire puis
 * lance la migration Non-Member → membre (règle G). Dédupliqué par eventId.
 * Rôles : `flashpay-service`, `flashpay-superadmin`.
 *
 * Complète (et à terme remplace) `Wallets/{id}/kyc-approval` qui n'agit que
 * sur un portefeuille à la fois.
 */
export const useApplyKycApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "wallets", "applyKycApproval"],
    mutationFn: (body: ApplyKycApprovalRequest) =>
      mutateData({
        url: walletEndpoints.walletsExt.applyKycApproval,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }),
    onSuccess: () => invalidateAfterMutation(queryClient, walletWriteCacheKeys()),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useApplyKycApproval failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
